import { useEffect, useState } from 'react';
import './league-dashboard.styles.scss';
import { Link, NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { listenToDoc } from '@/utils/getDataFromDb';
import { lineupEdge } from '@shared/chemistry.js';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { inOffseason, isPostseason, offseasonNext, phaseLabel } from '@/utils/leagueLabels';
import { nextGamesText } from '@/utils/nextGames';
import RankingsView from '@/components/SHARED/rankings-view/rankings-view.component';
import { Top25Context, top25From } from '@/utils/top25';
import { leagueTheme, useShellTheme } from '@/utils/theme';
import PostseasonView from '@/components/SHARED/postseason-view/postseason-view.component';
import Standings from '@/components/SHARED/standings/standings.component';
import ScheduleView from '@/components/SHARED/schedule-view/schedule-view.component';
import LeadersView from '@/components/SHARED/leaders-view/leaders-view.component';
import GameView from '@/components/SHARED/game-view/game-view.component';
import LeagueHistory from '@/components/SHARED/league-history/league-history.component';
import PastSeason from '@/components/SHARED/league-history/past-season.component';
import RecordBook from '@/components/SHARED/record-book/record-book.component';
import NewsView from '@/components/SHARED/news-view/news-view.component';
import PortalView from '@/components/SHARED/portal-view/portal-view.component';
import RecruitingView from '@/components/SHARED/recruiting-view/recruiting-view.component';
import TradeView from '@/components/SHARED/trade-view/trade-view.component';
import FrontOffice from '@/components/SHARED/front-office/front-office.component';
import DraftView from '@/components/SHARED/draft-view/draft-view.component';
import { portalVisible } from '@shared/portal.js';
import PlayerView from '@/components/SHARED/player-view/player-view.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import AdminTeam from './admin-team.component';
import LeagueSettings from './league-settings.component';

const LeagueDashboard = () => {

    const { leagueId } = useParams();
    const [ league, setLeague ] = useState(undefined);
    const [ teams, setTeams ] = useState(null);
    const [ schedule, setSchedule ] = useState(null);
    const [ leaders, setLeaders ] = useState(null);
    const [ teamStats, setTeamStats ] = useState(null);
    const [ postseason, setPostseason ] = useState(null);
    const [ rankings, setRankings ] = useState(null);
    const [ rankHistory, setRankHistory ] = useState(null);
    // League pages take the league's look (college or pro).
    useShellTheme(leagueTheme(league));
    const [ simming, setSimming ] = useState(null);
    const [ simMessage, setSimMessage ] = useState(null);

    useEffect(() => {
        const listeners = [];
        const listen = (path, callback) => listenToDoc({
            path, callback, 'handleListener': (unsub) => listeners.push(unsub),
        });
        listen(`leagues/${leagueId}`, setLeague);
        listen(`leagues/${leagueId}/summary/teams`, (d) => setTeams(d?.teams ?? null));
        listen(`leagues/${leagueId}/summary/schedule`, setSchedule);
        listen(`leagues/${leagueId}/summary/leaders`, setLeaders);
        listen(`leagues/${leagueId}/summary/teamStats`, setTeamStats);
        listen(`leagues/${leagueId}/summary/postseason`, setPostseason);
        listen(`leagues/${leagueId}/summary/rankings`, setRankings);
        listen(`leagues/${leagueId}/summary/rankHistory`, setRankHistory);
        return () => listeners.forEach(unsub => unsub());
    }, [leagueId])

    if (league === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (league === null) return <p className='meta'>League not found. <Link to='/admin/leagues'>Back to leagues</Link></p>;

    const base = `/admin/leagues/${leagueId}`;
    const season = league.season;
    const offseason = inOffseason(season);
    const inPostseason = isPostseason(season);
    const daysLeft = season.totalDays - season.day;
    const champion = season.champion && teams?.[season.champion];

    async function sim(days, label) {
        setSimming(label);
        setSimMessage(null);
        const res = await UseCloudFunction('simDays', { leagueId, days });
        setSimming(null);
        setSimMessage(res.error ? { error: true, text: res.error } : { text: res.games ? `${res.success} ${res.games} games played.` : res.success });
    }

    const teamLink = (id) => `${base}/teams/${id}`;
    const gameLink = (id) => `${base}/games/${id}`;
    const playerLink = (teamId, playerId) => `${base}/teams/${teamId}/players/${playerId}`;
    const periods = league.settings.minutesPerGame >= 48 ? 4 : 2;

    return (
        <div className='league-dashboard'>
            <Link to='/admin/leagues' className='back-link'><FontAwesomeIcon icon={faChevronLeft} /> Leagues</Link>
            <div className='g-card league-head'>
                <div className='league-head-text'>
                    <h2>{league.name}</h2>
                    <div className='meta'>
                        {league.type === 'pro' ? 'Pro' : 'College'} · {league.settings.teamCount} teams · {Object.keys(league.conferences).length} conferences
                    </div>
                    <div className='league-phase'>{phaseLabel(season, league.type)}</div>
                    {champion && <div className='meta'>Champion: <b>{champion.name}</b></div>}
                    {offseason && <div className='meta'>{offseasonNext(league)}</div>}
                    <div className='meta'>
                        {nextGamesText(league) ?
                            <>{offseason ? 'Next offseason step' : 'Next games'}: <b>{nextGamesText(league)}</b></> :
                            <>Daily games are off. <Link to={`${base}/settings`}>Turn them on</Link></>}
                    </div>
                </div>
                {offseason ?
                    <div className='sim-controls'>
                        <button className={`g-button primary ${simming ? 'disabled' : ''}`} disabled={Boolean(simming)} onClick={() => sim(1, 'step')}>
                            {simming === 'step' ? <ThreeDotsLoader /> : 'Next offseason step'}
                        </button>
                        <button className={`g-button ${simming ? 'disabled' : ''}`} disabled={Boolean(simming)} onClick={() => sim(15, 'offseason')}>
                            {simming === 'offseason' ? <ThreeDotsLoader /> : 'Finish the offseason'}
                        </button>
                    </div> :
                    <div className='sim-controls'>
                        <button className={`g-button primary ${simming ? 'disabled' : ''}`} disabled={Boolean(simming)} onClick={() => sim(1, 'day')}>
                            {simming === 'day' ? <ThreeDotsLoader /> : 'Sim next day'}
                        </button>
                        {!inPostseason && daysLeft > 1 &&
                            <button className={`g-button ${simming ? 'disabled' : ''}`} disabled={Boolean(simming)} onClick={() => sim(Math.min(7, daysLeft), 'week')}>
                                {simming === 'week' ? <ThreeDotsLoader /> : `Sim ${Math.min(7, daysLeft)} days`}
                            </button>
                        }
                        {!inPostseason && daysLeft > 7 &&
                            <button className={`g-button ${simming ? 'disabled' : ''}`} disabled={Boolean(simming)} onClick={() => sim(daysLeft, 'all')}>
                                {simming === 'all' ? <ThreeDotsLoader /> : 'Sim to end of regular season'}
                            </button>
                        }
                        {inPostseason &&
                            <button className={`g-button ${simming ? 'disabled' : ''}`} disabled={Boolean(simming)} onClick={() => sim(70, 'post')}>
                                {simming === 'post' ? <ThreeDotsLoader /> : 'Sim to the champion'}
                            </button>
                        }
                    </div>
                }
                {season.phase === 'complete' &&
                    <p className='meta season-done'>To replay this season instead, reset it in Settings before the offseason starts.</p>
                }
                {simming === 'all' && <p className='meta sim-note'>Simulating the rest of the regular season. This can take about a minute.</p>}
                {simMessage &&
                    <p className={`sim-message ${simMessage.error ? 'error' : ''}`} role='status'>{simMessage.text}</p>
                }
            </div>

            <nav className='league-tabs' aria-label='League'>
                <NavLink end to={base}>Standings</NavLink>
                <NavLink to={`${base}/news`}>News</NavLink>
                <NavLink to={`${base}/schedule`}>Schedule</NavLink>
                <NavLink to={`${base}/leaders`}>Leaders</NavLink>
                <NavLink to={`${base}/rankings`}>{league.type === 'pro' ? 'Power Rankings' : 'Rankings'}</NavLink>
                <NavLink to={`${base}/postseason`}>{league.type === 'pro' ? 'Playoffs' : 'Conference tournaments'}</NavLink>
                {league.type !== 'pro' && <NavLink to={`${base}/national`}>National tournament</NavLink>}
                {league.type !== 'pro' && <NavLink to={`${base}/recruiting`}>Recruiting</NavLink>}
                {portalVisible(league) && <NavLink to={`${base}/portal`}>Portal</NavLink>}
                {league.type === 'pro' && <NavLink to={`${base}/office`}>Free agents</NavLink>}
                {league.type === 'pro' && <NavLink to={`${base}/trades`}>Trades</NavLink>}
                {league.type === 'pro' && <NavLink to={`${base}/draft`}>Draft</NavLink>}
                <NavLink to={`${base}/history`}>History</NavLink>
                <NavLink to={`${base}/records`}>Records</NavLink>
                <NavLink to={`${base}/settings`}>Settings</NavLink>
            </nav>

            <Top25Context.Provider value={top25From(league, rankings)}>
            <Routes>
                <Route path='' element={<Standings league={league} teams={teams} teamLink={teamLink} />}></Route>
                <Route path='schedule' element={
                    <ScheduleView schedule={schedule} teams={teams} currentDay={season.day} gameLink={gameLink} />
                }></Route>
                <Route path='news' element={<NewsView league={league} teams={teams} teamLink={teamLink} gameLink={gameLink} playerLink={playerLink} />}></Route>
                <Route path='leaders' element={<AdminLeaders leagueId={leagueId} leaders={leaders} teamStats={teamStats} teams={teams} teamLink={teamLink} playerLink={playerLink} />}></Route>
                <Route path='rankings' element={<RankingsView league={league} rankings={rankings} teams={teams} teamLink={teamLink} />}></Route>
                <Route path='postseason' element={
                    <PostseasonView league={league} postseason={postseason} teams={teams} teamLink={teamLink} gameLink={gameLink}
                        part={league.type === 'pro' ? undefined : 'conference'} />
                }></Route>
                <Route path='national' element={
                    <PostseasonView league={league} postseason={postseason} teams={teams} teamLink={teamLink} gameLink={gameLink} part='national' />
                }></Route>
                <Route path='portal' element={<PortalView league={league} teams={teams} teamLink={teamLink} />}></Route>
                <Route path='recruiting' element={<RecruitingView league={league} teams={teams} teamLink={teamLink} />}></Route>
                <Route path='trades' element={<TradeView league={league} teams={teams} teamLink={teamLink} />}></Route>
                <Route path='office' element={<FrontOffice league={league} teams={teams} teamLink={teamLink} />}></Route>
                <Route path='draft' element={<DraftView league={league} teams={teams} teamLink={teamLink} playerLink={playerLink} />}></Route>
                <Route path='history' element={<LeagueHistory league={league} />}></Route>
                <Route path='records' element={<RecordBook league={league} gameLink={gameLink} seasonLink={(year) => `${base}/history/${year}`} />}></Route>
                <Route path='history/:year' element={<PastSeason league={league} teamLink={teamLink} gameLink={gameLink} />}></Route>
                <Route path='settings' element={<LeagueSettings league={league} />}></Route>
                <Route path='teams/:teamId' element={<AdminTeam league={league} teams={teams ?? {}} schedule={schedule} base={base} rankHistory={rankHistory} />}></Route>
                <Route path='teams/:teamId/players/:playerId' element={
                    <AdminPlayerRoute leagueId={leagueId} teams={teams} teamLink={teamLink} gameLink={gameLink} />
                }></Route>
                <Route path='games/:gameId' element={<GameRoute leagueId={leagueId} periods={periods} teamLink={teamLink} playerLink={playerLink} league={league} teamStats={teamStats} />}></Route>
                <Route path='*' element={<Navigate replace to={base} />}></Route>
            </Routes>
            </Top25Context.Provider>
        </div>
    )
}

const GameRoute = ({ leagueId, periods, teamLink, playerLink, league, teamStats }) => {
    const { gameId } = useParams();
    return <GameView leagueId={leagueId} gameId={gameId} periodsInRegulation={periods} teamLink={teamLink} playerLink={playerLink} league={league} teamStats={teamStats} />;
}

// Admins also see the player's hidden traits.
const AdminPlayerRoute = ({ leagueId, teams, teamLink, gameLink }) => {
    const { teamId, playerId } = useParams();
    const [ hidden, setHidden ] = useState(null);
    useEffect(() => {
        let unsub;
        listenToDoc({
            'path': `leagues/${leagueId}/private/traits`,
            'callback': (d) => setHidden(d?.teams?.[teamId]?.players?.[playerId] ?? null),
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); }
    }, [leagueId, teamId, playerId])
    return <PlayerView leagueId={leagueId} teamId={teamId} playerId={playerId} teams={teams} teamLink={teamLink} gameLink={gameLink} hidden={hidden} />;
}

// Admins also see each team's hidden chemistry next to its stats: the whole
// roster's fit minus neutral (what the grade is built from).
const AdminLeaders = ({ leagueId, ...props }) => {
    const [ chemistry, setChemistry ] = useState(null);
    useEffect(() => {
        let unsub;
        listenToDoc({
            'path': `leagues/${leagueId}/private/traits`,
            'callback': (d) => setChemistry(Object.fromEntries(Object.entries(d?.teams ?? {})
                .map(([id, t]) => [id, lineupEdge(Object.values(t.players ?? {}).map(p => p.chemistry), t.chemistry)]))),
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); }
    }, [leagueId])
    return <LeadersView {...props} chemistry={chemistry} />;
}

export default LeagueDashboard
